import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { parseLocalDate, isValidDate, DEFAULT_ENCOUNTER_FIELDS_CONFIG } from '@claim-os/shared';
import type { FormFieldSetting } from '@claim-os/shared';
import { buildSubmitClaimSchema } from './buildSubmitClaimSchema';
import type { SubmitClaimForm } from './schema';

type ClaimTypeFields = Record<string, Record<string, FormFieldSetting>>;

/**
 * Builds a resolver bound to the practice's encounter fields config. Wraps
 * zodResolver and always enforces the DOS ≥ DOB cross-field rule, even when
 * other required fields are still empty.
 *
 * Zod skips chained `.refine()` when the base schema returns INVALID (e.g.
 * when `insurer` or `memberId` are empty), so zodResolver never emits the
 * DOS < DOB error until every other required field is valid too. Injecting
 * the check directly into the resolver result ensures it shows on every
 * `onChange` cycle, giving the user immediate feedback as soon as both dates
 * are filled regardless of the rest of the form state.
 */
export function makeSubmitClaimResolver(
  encounterFields: Record<string, FormFieldSetting>,
  claimTypeFields?: ClaimTypeFields,
): Resolver<SubmitClaimForm> {
  const { submitClaimSchema } = buildSubmitClaimSchema(encounterFields, claimTypeFields);

  return async (values, ctx, options) => {
    const result = await zodResolver(submitClaimSchema)(values, ctx, options);

    const dob = parseLocalDate((values.dateOfBirth as string) ?? '');
    const dos = parseLocalDate((values.dateOfService as string) ?? '');

    if (
      values.dateOfBirth &&
      values.dateOfService &&
      isValidDate(dob) &&
      isValidDate(dos) &&
      dos < dob
    ) {
      result.errors.dateOfService = {
        type: 'custom',
        message: 'Date of service cannot be before patient date of birth',
      };
    } else if (
      result.errors.dateOfService?.type === 'custom' &&
      result.errors.dateOfService.message === 'Date of service cannot be before patient date of birth'
    ) {
      // Clear if it was only a custom cross-field error and dates are now valid.
      delete result.errors.dateOfService;
    }

    return result;
  };
}

/** Default (unconfigured) resolver — mirrors today's form. */
export const submitClaimResolver: Resolver<SubmitClaimForm> = makeSubmitClaimResolver(
  DEFAULT_ENCOUNTER_FIELDS_CONFIG.encounterFields,
);
